// ── company-linked-section: linked programs + cohorts panels (own honest-state)
// Wave 1 (Companies). Each panel owns its OWN useHonestQuery → <HonestState>
// read, so one list being unavailable / permission-denied / empty never blanks
// the company detail and never reads as a fabricated empty. Reads are the bare
// list services filtered to this company id (the lists are server-capped arrays,
// no pagination); the FE filter is belt-and-suspenders over the company scope.

import { Module, ModuleBody, ModuleHead, Badge, humanizeLabel } from "@vjal/ui";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingCohortDto, CoachingProgramDto } from "../../services/coaching-dtos";

const services = getCoachingServices();

interface LinkedRow {
  id: string;
  name: string;
  status: string;
}

function LinkedList({ rows, noun }: { rows: LinkedRow[]; noun: string }) {
  return (
    <ul className="m-0 flex flex-col gap-2 p-0" aria-label={`Linked ${noun}`}>
      {rows.map((row) => (
        <li key={row.id} className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2">
          <span className="text-sm font-medium text-strong">{row.name}</span>
          <Badge variant={row.status === "active" ? "success" : "warning"}>{humanizeLabel(row.status)}</Badge>
        </li>
      ))}
    </ul>
  );
}

export function CompanyProgramsSection({ companyId }: { companyId: string }) {
  const query = useHonestQuery(
    coachingQueryKeys.list("programs.list", { company_id: companyId }),
    () => services.programs.list({ company_id: companyId }),
    { isEmpty: (data) => programRowsFor(companyId, data.items).length === 0 }
  );
  const rows = programRowsFor(companyId, query.data?.items ?? []);

  return (
    <Module span={6}>
      <ModuleHead title="Programs" subtitle="Programs owned by this company." />
      <ModuleBody>
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No programs yet" : undefined}
            message={query.posture === "empty" ? "Programs created for this company will appear here." : undefined}
          />
        ) : (
          <LinkedList rows={rows} noun="programs" />
        )}
      </ModuleBody>
    </Module>
  );
}

export function CompanyCohortsSection({ companyId }: { companyId: string }) {
  const query = useHonestQuery(
    coachingQueryKeys.list("cohorts.list", { company_id: companyId }),
    () => services.cohorts.list({ company_id: companyId }),
    { isEmpty: (data) => cohortRowsFor(companyId, data.items).length === 0 }
  );
  const rows = cohortRowsFor(companyId, query.data?.items ?? []);

  return (
    <Module span={6}>
      <ModuleHead title="Cohorts" subtitle="Cohorts running under this company." />
      <ModuleBody>
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No cohorts yet" : undefined}
            message={query.posture === "empty" ? "Cohorts created for this company will appear here." : undefined}
          />
        ) : (
          <LinkedList rows={rows} noun="cohorts" />
        )}
      </ModuleBody>
    </Module>
  );
}

function programRowsFor(companyId: string, programs: CoachingProgramDto[]): LinkedRow[] {
  return programs
    .filter((program) => program.company_id == null || program.company_id === companyId)
    .map((program) => ({ id: program.id, name: program.name, status: program.status ?? "active" }));
}

function cohortRowsFor(companyId: string, cohorts: CoachingCohortDto[]): LinkedRow[] {
  return cohorts
    .filter((cohort) => cohort.company_id == null || cohort.company_id === companyId)
    .map((cohort) => ({ id: cohort.id, name: cohort.name, status: cohort.status ?? "active" }));
}
